import { ComponentType, FC, useEffect, useMemo, useRef, useState } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { Dialog, useMediaQuery, useTheme } from "@mui/material"
import { get, isNil } from "lodash"
import dayjs from "config/dayjs"
import { useVenue } from "providers/venue"
import { useKioskConfig } from "providers/venue/modules/data/kiosk-config/useKioskConfig"
import { SectionData } from "providers/venue/types"
import { useParkingFinder } from "hooks/parking-finder"
import { Q_PARKING_REF_ID } from "hooks/parking-finder/constants"
import { TABLET_MAX_BREAKPOINT } from "extensions/fashion-promenade-terminal/constant"
import { DurationUnitsObjectType } from "services/venue"
import { StandardParkingFinderModal } from "components/domains/parkingFinder/StandardParkingFinderModal"
import { gtmPushDataLayerConfirmParking } from "utils/googletagmanager"

export type ParkingFinderModalProps = {
  //todo: might change to optional
  note: string
  onChangeTextField: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void
  closeModal: () => void
  handleSave: () => void
  data: SectionData
  hasActiveData: boolean
}

type InnerProps = {
  expire: DurationUnitsObjectType
  renderModal?: ComponentType<ParkingFinderModalProps>
}

export const ParkingFinderContainer = ({
  renderModal = StandardParkingFinderModal,
}) => {
  const theme = useTheme()
  const isUpMd = useMediaQuery(theme.breakpoints.up(TABLET_MAX_BREAKPOINT))
  const { parkingFinderConfig } = useVenue()

  if (isUpMd || !parkingFinderConfig?.enable) return null

  return (
    <ParkingFinderContainerInner
      expire={parkingFinderConfig.expire}
      renderModal={renderModal}
    />
  )
}

const ParkingFinderContainerInner: FC<InnerProps> = ({
  expire,
  renderModal: RenderComponent = StandardParkingFinderModal,
}) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const { findSectionByReferenceId } = useVenue()
  const { save, isActive: isParkingActive, savedRecord } = useParkingFinder()
  const { kioskConfig } = useKioskConfig()
  const shouldOpen = useRef(true)
  const theme = useTheme()
  const styleOverrides =
    theme.components[`ParkingFinderContainer`]?.styleOverrides || {}
  const { root } = styleOverrides

  const navigate = useNavigate()

  const [note, setNote] = useState("")

  const paramValue = searchParams.get(Q_PARKING_REF_ID)

  const parkingRefId = useMemo(
    () => get(kioskConfig, "source.properties.parkingRefId"),
    [kioskConfig]
  )

  const candidateSection = useMemo(
    () => (paramValue ? findSectionByReferenceId(paramValue) ?? null : null),
    [paramValue, findSectionByReferenceId]
  )

  useEffect(() => {
    if (!parkingRefId) return // kiosk config has no parkingRefId configured
    if (savedRecord?.referenceId === parkingRefId) return // user already saved this parking; don't re-prompt
    if (!shouldOpen.current) return
    // Use functional form so `searchParams` is not a dep — keeping it out
    // prevents the effect from re-firing when closeModal deletes the param.
    setSearchParams(
      (prev) => {
        if (prev.has(Q_PARKING_REF_ID)) return prev
        const next = new URLSearchParams(prev)
        next.set(Q_PARKING_REF_ID, parkingRefId)
        return next
      },
      { replace: true }
    )
    shouldOpen.current = false
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parkingRefId, savedRecord])

  const closeModal = () => {
    if (searchParams.has(Q_PARKING_REF_ID)) {
      const next = new URLSearchParams(searchParams)
      next.delete(Q_PARKING_REF_ID)
      setSearchParams(next, { replace: true })
    }
    setNote("")
  }

  const navigateToParkingOrdinal = () => {
    const ordinal = candidateSection?.properties?.ordinal

    if (isNil(ordinal)) {
      closeModal()
      return
    }

    const next = new URLSearchParams(searchParams)
    next.delete(Q_PARKING_REF_ID)
    const search = next.toString()
    navigate(`/maps/ordinal/${ordinal}${search ? `?${search}` : ""}`, {
      replace: true,
    })
    setNote("")
  }

  const handleSave = () => {
    if (!paramValue) return
    const previousParkingName = isParkingActive
      ? findSectionByReferenceId(savedRecord.referenceId)?.properties?.name
          ?.en ?? ""
      : ""
    const expiresAt = dayjs().add(dayjs.duration(expire)).valueOf()
    save(paramValue, note, expiresAt)
    gtmPushDataLayerConfirmParking(candidateSection, previousParkingName)
    navigateToParkingOrdinal()
  }

  return (
    <Dialog open={!!candidateSection} onClose={closeModal} sx={root}>
      <RenderComponent
        note={note}
        onChangeTextField={(e) => setNote(e.target.value)}
        closeModal={closeModal}
        handleSave={handleSave}
        data={candidateSection}
        hasActiveData={isParkingActive}
      />
    </Dialog>
  )
}
